import { ApiError } from '../../api/client';

export interface WithdrawalBody {
  circle: number;
  payee: string;
  requisites: { name: string; tax_id: string };
  invoice_upload_id: string;
}
export interface WithdrawalIntent {
  owner: number;
  key: string;
  body: WithdrawalBody;
}
const storageKey = (owner: number) => `steos:partner:withdrawal:${owner}`;
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const uploadPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function readWithdrawalIntent(owner: number): WithdrawalIntent | null {
  const raw = sessionStorage.getItem(storageKey(owner));
  if (!raw) return null;
  const value = JSON.parse(raw) as WithdrawalIntent;
  if (
    value.owner !== owner ||
    !uuidPattern.test(value.key) ||
    !value.body ||
    !Number.isSafeInteger(value.body.circle) ||
    value.body.circle <= 0 ||
    typeof value.body.payee !== 'string' ||
    !/^[a-z0-9_-]{1,32}$/.test(value.body.payee) ||
    typeof value.body.requisites?.name !== 'string' ||
    !value.body.requisites.name.trim() ||
    value.body.requisites.name.length > 200 ||
    typeof value.body.requisites.tax_id !== 'string' ||
    !value.body.requisites.tax_id.trim() ||
    value.body.requisites.tax_id.length > 64 ||
    !uploadPattern.test(value.body.invoice_upload_id)
  )
    throw new Error('withdrawal_intent_invalid');
  return value;
}

export function saveWithdrawalIntent(intent: WithdrawalIntent) {
  // Без сохранённого тела и ключа нельзя безопасно восстановить финансовый POST после перезагрузки.
  sessionStorage.setItem(storageKey(intent.owner), JSON.stringify(intent));
}
export const clearWithdrawalIntent = (owner: number) => sessionStorage.removeItem(storageKey(owner));

export const definitiveWithdrawalError = (error: unknown) =>
  error instanceof ApiError &&
  [400, 403, 404, 409, 422, 428].includes(error.status) &&
  [
    'withdrawal_invalid',
    'withdraw_below_min',
    'withdraw_exceeds',
    'requisites_invalid',
    'withdrawal_invoice_not_ready',
    'withdraw_level_required',
    'steos_id_required',
    'partner_payout_policy_missing',
    'partner_production_payout_disabled',
  ].includes(error.code);

export function validateInvoiceFile(file: File): string | null {
  if (file.size < 1 || file.size > 10 * 1024 * 1024) return 'Размер счёта должен быть от 1 байта до 10 МБ.';
  if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) return 'Выберите PDF, JPG или PNG.';
  return null;
}

export function validateWithdrawal(body: WithdrawalBody, min: number, max: number, currency: string): string | null {
  if (!Number.isSafeInteger(body.circle) || body.circle < min || body.circle > max)
    return 'Проверьте сумму вывода: целое число в доступном диапазоне.';
  if (!body.requisites.name.trim() || body.requisites.name.length > 200) return 'Укажите имя получателя: до 200 символов.';
  if (
    !body.requisites.tax_id.trim() ||
    body.requisites.tax_id.length > 64 ||
    (currency === 'RUB' && !/^[0-9]{10,15}$/.test(body.requisites.tax_id))
  )
    return 'Проверьте налоговый номер получателя.';
  if (!uploadPattern.test(body.invoice_upload_id)) return 'Загрузите проверенный счёт на оплату.';
  return null;
}
